import $ from 'jquery'
import _ from 'lodash'
import globals from 'scoreboard/components/globals'
import BaseRouter from 'scoreboard/components/base-router'
import FormsNav from './forms-nav'
import Form from './model/form'
import FormType from './model/form-type'
const {
	FORM, PAGE, FOLDER, DIALOG, NAVIGATION_TABS
} = FormType
import FormsEdit from './edit/forms-edit'
import FormsOverview from './overview/forms-overview'
import FormsFolderOverview from './overview/forms-folder-overview'
import PagesEdit from './edit/pages-edit'
import FolderForm from 'scoreboard/components/folder-form'
import appDisplay from 'scoreboard/components/app-display'
import viewEditTabs from 'scoreboard/components/view-edit-tabs.hbs'
import EmptyState from 'scoreboard/components/empty-state'
import conductor from 'scoreboard/components/conductor'
import EmbeddedForm from './embedded-form'
import persistedViewState from 'scoreboard/components/persisted-view-state'
import getCreateFolderFunction from 'scoreboard/components/get-create-folder-function'
import NavigationTabsForm from './edit/navigation-tabs-form'
import DialogsEditDatasetForm from './edit/dialogs-edit-dataset-form'
import DialogsEditWidgetsForm from './edit/dialogs-edit-widgets-form'

const FormsRouter = BaseRouter.extend({

	routes: {
		'forms/new(/)': 'create',
		'forms/:id(/)': 'overview',
		'forms/:id/edit(/)': 'edit',
		'forms/:id/new(/)': 'create',
		'forms/:id/new-folder(/)': 'createFolder',
		'organizations/:orgId/forms/new(/)': 'createFirst',
		'organizations/:orgId/forms/new-folder(/)': 'createFirstFolder',
		'forms/:id/new-page(/)': 'createPage',
		'forms/:id/new-dialog(/)': 'createDialog',
		'forms/:id/new-navigation-tabs(/)': 'createNavigationTabs',
		'forms/:id/shared/:token(/)': 'embeddedOverview'
	},

	Nav: FormsNav,
	Model: Form,
	OverviewView: FormsOverview,
	EditView: FormsEdit,
	FolderOverview: FormsFolderOverview,
	EmbeddedModel: EmbeddedForm,

	section: 'forms',

	// orgId is used when creating a top-level form by the createFirst
	// function below
	create: function(relativeId, orgId) {
		if (this.handleLicenseRestriction({ section: 'forms' })) {
			return
		}
		$.when(this._resolveOrg(relativeId, orgId)).done(() => {
			const model = new Form({
				organizationId: globals.orgId,
				type: FORM,
				relativeId
			})
			const form = new FormsEdit({ model })
			this.navForNewModel({ relativeId, orgId, form, model })
		})
	},

	createFirst: function(orgId) {
		this.create(null, orgId)
	},

	overview: function(id) {
		if (this.handleLicenseRestriction({ section: 'forms' })) {
			return
		}
		const modelPromise = this.load(id)
		appDisplay.display(modelPromise, model => {
			// #34020 clicking on a page, navigation, or dialog tab overview
			// should go to root form overview
			if ([PAGE, NAVIGATION_TABS, DIALOG].includes(model.get('type'))) {
				let rootId = model.get('rootFormId')
				this.overview(rootId)
				conductor.navigate(`forms/${rootId}`)
				// #59889
				// If we're rerouting to the root form, we don't want start
				// attempting to display the current form. Otherwise, we might
				// execute async display actions that won't complete until
				// their original form is no longer displayed.
				return
			}
			let View = model.get('type') == FOLDER ?
				this.FolderOverview : this.OverviewView

			return new View({
				model: model,
				tabs: viewEditTabs,
				overviewTabCode: 'common.view'
			})
		})
		this.navForModel({ id }, modelPromise)
	},

	edit: function(id) {
		if (this.handleLicenseRestriction({ section: 'forms' })) {
			return
		}
		const modelPromise = this.load(id)
		appDisplay.display(modelPromise, model => {
			if (!model.isEditable()) {
				let body = this._notEditableBody(model)
				return new EmptyState({ body })
			}

			let type = model.get('type')
			if (type === FOLDER) {
				return new FolderForm({
					model: model,
					tabs: viewEditTabs,
					overviewTabCode: 'common.view'
				})
			} else {
				let editView
				if (type === FORM) {
					editView = new FormsEdit({ model })
				} else if (type === NAVIGATION_TABS) {
					editView = new NavigationTabsForm({ model })
				} else if (type === DIALOG) {
					editView = new DialogsEditWidgetsForm({ model })
				} else {
					editView = new PagesEdit({ model })
				}
				return editView
			}
		})

		this.navForModel({ id, edit: true }, modelPromise).then(navView => {
			this.ensurePagesVisible(navView, modelPromise)
		})
	},

	/**
	 * Ensures the pages under a form are visible in the nav pane
	 */
	ensurePagesVisible: function(navView, modelPromise) {
		navView._onceTreeLoaded(() => {
			modelPromise.then(model => {
				const { type } = model.attributes
				// make sure the pages under the form are visible
				if ([FORM, PAGE, NAVIGATION_TABS, DIALOG].includes(type)) {
					navView.gardener.onChangeEditViewState(true)
				}
			})
		})
	},

	createFolder: getCreateFolderFunction(false),

	createFirstFolder: function(orgId) {
		this.createFolder(null, orgId)
	},

	createPage: function(relativeId) {
		this.createFormItem(relativeId, PAGE, PagesEdit)
	},

	createDialog: function(relativeId) {
		this.createFormItem(relativeId, DIALOG, DialogsEditDatasetForm)
	},

	createNavigationTabs: function(relativeId) {
		this.createFormItem(relativeId, NAVIGATION_TABS, NavigationTabsForm)
	},

	createFormItem: function(relativeId, type, EditForm) {
		if (this.handleLicenseRestriction({ section: 'forms' })) {
			return
		}
		const modelPromise = this.load(relativeId)
		appDisplay.display(modelPromise, parentForm => {
			let organizationId = globals.orgId = parentForm.get('organizationId')
			// figure out the parents
			// this is a lot to not copy, but this way we pick up any new
			// properties that are added
			let doNotCopy = ['widgets', 'label', 'id', 'leftBound',
				'rightBound', 'startingFormId', 'index', 'children',
				'parentId', 'rootId', 'rootFormId', 'statements' ]
			let parentAttributes = _.omit(parentForm.attributes, doNotCopy)

			let page = new Form(_.defaults({
				relativeId,
				organizationId,
				type,
				icon: type
			}, parentAttributes))

			const form = new EditForm({
				model: page,
				tabs: viewEditTabs,
				overviewTabCode: 'common.view'
			})

			this.navForNewModel({
				relativeId, orgId: organizationId, form, model: page
			}).then(navView => {
				this.ensurePagesVisible(navView, modelPromise)
			})

			return form
		})
	},

	embeddedOverview: function(id, token) {
		this.Model = this.EmbeddedModel
		appDisplay.display(this.load({id, token}), (model) => {
			return this.createEmbeddedOverview(model, token)
		})
	},

	createEmbeddedOverview: function (model, embeddedToken) {
		let state = persistedViewState(model.get('uiState'))
		return new this.OverviewView({
			model,
			embeddedToken,
			state
		})
	}

})

export default FormsRouter

FormsRouter.__filename__ = 'forms/forms-router.js'
